import React, { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Box,
  Button,
  IconButton,
  Chip,
  Menu,
  MenuItem,
  ListItemIcon,
  ListItemText,
  FormControlLabel,
  Switch,
  TextField,
  FormControl,
  InputLabel,
  Select,
  InputAdornment,
  Tooltip,
} from '@mui/material';
import { DataGrid } from '@mui/x-data-grid';
import {
  Contacts as ContactsIcon,
  Add as AddIcon,
  Refresh as RefreshIcon,
  MoreVert as MoreVertIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Visibility as VisibilityIcon,
  Block as BlockIcon,
  Search as SearchIcon,
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { useContacts } from '../hooks/useContacts';
import { useSnackbar } from '../../../hooks/mui/useSnackBar';
import { useConfirmDialog } from '../../../hooks/mui/useConfirmDialog';
import { usePermissions } from '../../auth/hooks/usePermissions';
import { CONTACT_TYPES } from '../utils/enums';

import PageHeader from '../../../components/common/PageHeader';

const TYPE_COLORS = {
  person: 'info',
  company: 'primary',
  authority: 'warning',
  vendor: 'default',
};

export default function ContactListPage() {
  const { t } = useTranslation(['contacts', 'common']);
  const navigate = useNavigate();
  const { showSuccess, showError } = useSnackbar();
  const { showConfirmDialog, ConfirmDialog } = useConfirmDialog();
  const permissions = usePermissions();

  const [searchTerm, setSearchTerm] = useState('');
  const [contactType, setContactType] = useState('');
  const [includeInactive, setIncludeInactive] = useState(false);
  const [paginationModel, setPaginationModel] = useState({ page: 0, pageSize: 25 });
  const [actionMenuAnchor, setActionMenuAnchor] = useState(null);
  const [selectedContact, setSelectedContact] = useState(null);

  const { contacts, loading, error, clearError, fetchContacts, deleteContact, deactivateContact } = useContacts();
  const debounceRef = useRef(null);

  const runFetch = useCallback(() => {
    const params = { include_inactive: includeInactive };
    if (contactType) params.contact_type = contactType;
    fetchContacts(params);
  }, [fetchContacts, includeInactive, contactType]);

  useEffect(() => {
    clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(runFetch, 0);
    return () => clearTimeout(debounceRef.current);
  }, [contactType, includeInactive, runFetch]);

  const filteredContacts = useMemo(() => {
    if (!searchTerm.trim()) return contacts;
    const q = searchTerm.toLowerCase();
    return contacts.filter(
      (c) =>
        c.name.toLowerCase().includes(q) ||
        (c.att && c.att.toLowerCase().includes(q)) ||
        (c.role && c.role.toLowerCase().includes(q))
    );
  }, [contacts, searchTerm]);

  const handleMenuOpen = useCallback((event, contact) => {
    event.stopPropagation();
    setSelectedContact(contact);
    setActionMenuAnchor(event.currentTarget);
  }, []);

  const handleMenuClose = useCallback(() => {
    setActionMenuAnchor(null);
    setSelectedContact(null);
  }, []);

  const handleDelete = useCallback((contact) => {
    showConfirmDialog({
      title: t('contacts:confirm.delete_title'),
      message: t('contacts:confirm.delete_message', { name: contact.name }),
      confirmText: t('common:buttons.delete'),
      cancelText: t('common:buttons.cancel'),
      severity: 'error',
      onConfirm: async () => {
        const result = await deleteContact(contact.contact_id);
        if (result.success) {
          showSuccess(t('contacts:success.deleted'));
          runFetch();
        } else {
          showError(result.error || t('contacts:errors.delete_failed'));
        }
      },
    });
    handleMenuClose();
  }, [showConfirmDialog, deleteContact, showSuccess, showError, handleMenuClose, runFetch, t]);

  const handleDeactivate = useCallback(async (contact) => {
    handleMenuClose();
    const result = await deactivateContact(contact.contact_id);
    if (result.success) {
      showSuccess(t('contacts:success.deactivated'));
    } else {
      showError(result.error || t('contacts:errors.deactivate_failed'));
    }
  }, [deactivateContact, showSuccess, showError, handleMenuClose, t]);

  const canCreate = permissions.hasScope('contact:create');
  const canUpdate = permissions.hasScope('contact:update');
  const canDelete = permissions.hasScope('contact:delete');

  const columns = useMemo(() => [
    {
      field: 'name',
      headerName: t('contacts:grid.name'),
      flex: 1,
      minWidth: 200,
    },
    {
      field: 'att',
      headerName: t('contacts:grid.att'),
      flex: 1,
      minWidth: 150,
      renderCell: (params) => params.value || '—',
    },
    {
      field: 'contact_type',
      headerName: t('contacts:grid.type'),
      width: 130,
      renderCell: (params) => (
        <Chip
          label={t(`contacts:types.${params.value}`)}
          color={TYPE_COLORS[params.value] || 'default'}
          size="small"
        />
      ),
    },
    {
      field: 'role',
      headerName: t('contacts:grid.role'),
      flex: 1,
      minWidth: 160,
      renderCell: (params) => params.value || '—',
    },
    {
      field: 'top_phone',
      headerName: t('contacts:grid.phone'),
      width: 160,
      renderCell: (params) => params.value || '—',
    },
    {
      field: 'top_email',
      headerName: t('contacts:grid.email'),
      flex: 1,
      minWidth: 200,
      renderCell: (params) => params.value || '—',
    },
    {
      field: 'is_active',
      headerName: t('contacts:grid.status'),
      width: 110,
      renderCell: (params) => (
        <Chip
          label={params.value ? t('common:status.active') : t('common:status.inactive')}
          color={params.value ? 'success' : 'default'}
          size="small"
        />
      ),
    },
    {
      field: 'actions',
      headerName: '',
      width: 60,
      sortable: false,
      renderCell: (params) => (
        <IconButton size="small" onClick={(e) => handleMenuOpen(e, params.row)}>
          <MoreVertIcon fontSize="small" />
        </IconButton>
      ),
    },
  ], [t, handleMenuOpen]);

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', flex: 1, minHeight: 0 }}>
      <PageHeader
        icon={ContactsIcon}
        title={t('contacts:page_title')}
        subtitle={t('contacts:page_subtitle')}
        actions={<>
          <Tooltip title={t('common:buttons.refresh')}>
            <IconButton onClick={runFetch}>
              <RefreshIcon />
            </IconButton>
          </Tooltip>
          {canCreate && (
            <Button variant="contained" startIcon={<AddIcon />} onClick={() => navigate('create')}>
              {t('contacts:actions.create')}
            </Button>
          )}
        </>}
      />

        {/* Filters */}
        <Box display="flex" flexWrap="wrap" gap={2} mb={2}>
          <TextField
            size="small"
            placeholder={t('contacts:filters.search_placeholder')}
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            InputProps={{ startAdornment: <InputAdornment position="start"><SearchIcon fontSize="small" /></InputAdornment> }}
            sx={{ minWidth: 240 }}
          />
          <FormControl size="small" sx={{ minWidth: 160 }}>
            <InputLabel id="contact-list-page-type-label">{t('contacts:filters.type')}</InputLabel>
            <Select labelId="contact-list-page-type-label"
              value={contactType}
              label={t('contacts:filters.type')}
              onChange={(e) => setContactType(e.target.value)}
            >
              <MenuItem value="">{t('contacts:filters.all_types')}</MenuItem>
              {CONTACT_TYPES.map((ct) => (
                <MenuItem key={ct.value} value={ct.value}>{t(ct.labelKey)}</MenuItem>
              ))}
            </Select>
          </FormControl>
          <FormControlLabel
            control={<Switch checked={includeInactive} onChange={(e) => setIncludeInactive(e.target.checked)} />}
            label={t('contacts:filters.include_inactive')}
          />
        </Box>

        <DataGrid
          rows={filteredContacts}
          columns={columns}
          getRowId={(row) => row.contact_id}
          loading={loading}
          paginationModel={paginationModel}
          onPaginationModelChange={setPaginationModel}
          pageSizeOptions={[25, 50, 100]}
          disableColumnFilter
          disableColumnMenu
          disableColumnResize
          disableRowSelectionOnClick
          onRowClick={(params) => navigate(`${params.row.contact_id}`)}
          sx={{
            '& .MuiDataGrid-row': { cursor: 'pointer' },
            '& .MuiDataGrid-cell': {
              minHeight: 'unset !important',
              maxHeight: 'none !important',
              height: 'auto !important',
              display: 'flex !important',
              alignItems: 'center !important',
              padding: '8px 16px !important',
            },
            minHeight: 400,
          }}
        />

        <Menu anchorEl={actionMenuAnchor} open={Boolean(actionMenuAnchor)} onClose={handleMenuClose}>
          <MenuItem onClick={() => { navigate(`${selectedContact?.contact_id}`); handleMenuClose(); }}>
            <ListItemIcon><VisibilityIcon fontSize="small" /></ListItemIcon>
            <ListItemText>{t('common:actions.view')}</ListItemText>
          </MenuItem>
          {canUpdate && (
            <MenuItem onClick={() => { navigate(`${selectedContact?.contact_id}/edit`); handleMenuClose(); }}>
              <ListItemIcon><EditIcon fontSize="small" /></ListItemIcon>
              <ListItemText>{t('common:actions.edit')}</ListItemText>
            </MenuItem>
          )}
          {canUpdate && selectedContact?.is_active && (
            <MenuItem onClick={() => handleDeactivate(selectedContact)}>
              <ListItemIcon><BlockIcon fontSize="small" /></ListItemIcon>
              <ListItemText>{t('contacts:actions.deactivate')}</ListItemText>
            </MenuItem>
          )}
          {canDelete && (
            <MenuItem onClick={() => handleDelete(selectedContact)} sx={{ color: 'error.main' }}>
              <ListItemIcon><DeleteIcon fontSize="small" color="error" /></ListItemIcon>
              <ListItemText>{t('common:actions.delete')}</ListItemText>
            </MenuItem>
          )}
        </Menu>

        <ConfirmDialog />
    </Box>
  );
}
